// src/pages/InscriptionVendeur.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { db, storage } from "../firebase";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  increment,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

/* =========================
   PACKS
========================= */
const PACKS = {
  standard: {
    id: "standard",
    name: "Pack Standard",
    price: "0 FCFA / mois*",
    subtitle: "Commission sur chaque vente",
    description:
      "Idéal pour démarrer sans frais mensuels. Vous payez uniquement une commission sur chaque vente, selon la catégorie de produit.",
    billingUnit: "month",
    unitCount: 1,
    unitPrice: 0,
    commissionRate: 10,
    isFree: true,
    allowQuantity: false,
    minQuantity: 1,
    maxQuantity: 1,
  },

  standard_plus: {
    id: "standard_plus",
    name: "Pack Standard+",
    price: "5 000 FCFA / mois*",
    subtitle: "Sans commission sur les ventes",
    description:
      "Pour les vendeurs actifs : outils essentiels (opérations en masse, stats basiques, amélioration de boutique) sans commission prélevée sur vos ventes.",
    billingUnit: "month",
    unitCount: 1,
    unitPrice: 5000,
    commissionRate: 0,
    isFree: false,
    allowQuantity: true,
    minQuantity: 1,
    maxQuantity: 24,
  },

  pro_semestre: {
    id: "pro_semestre",
    name: "Pack Pro (Semestriel)",
    price: "10 000 FCFA / 6 mois*",
    subtitle: "Sans commission + outils avancés",
    description:
      "Pack Pro avec paiement tous les 6 mois : outils avancés, publicité, automatisation, API, fulfillment, support prioritaire, sans commission sur vos ventes.",
    billingUnit: "semester",
    unitCount: 6,
    unitPrice: 10000,
    commissionRate: 0,
    isFree: false,
    allowQuantity: true,
    minQuantity: 1,
    maxQuantity: 6,
  },

  pro: {
    id: "pro",
    name: "Pack Pro (Annuel)",
    price: "15 000 FCFA / an*",
    subtitle: "Meilleure valeur + 0% commission",
    description:
      "Le meilleur rapport qualité/prix : Pro complet + paiement annuel. Automatisation, publicité, API, fulfillment, support prioritaire, sans commission sur vos ventes.",
    billingUnit: "year",
    unitCount: 12,
    unitPrice: 15000,
    commissionRate: 0,
    isFree: false,
    allowQuantity: true,
    minQuantity: 1,
    maxQuantity: 5,
    featured: true,
  },
};

const baseButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px 18px",
  borderRadius: "999px",
  fontSize: "14px",
  fontWeight: 700,
  textDecoration: "none",
  border: "none",
  whiteSpace: "nowrap",
  cursor: "pointer",
};

const AFRICAN_COUNTRIES = [
  { code: "TG", name: "Togo", dialCode: "+228", min: 8, max: 8 },
  { code: "BJ", name: "Bénin", dialCode: "+229", min: 8, max: 10 },
  { code: "CI", name: "Côte d’Ivoire", dialCode: "+225", min: 10, max: 10 },
  { code: "GH", name: "Ghana", dialCode: "+233", min: 9, max: 9 },
  { code: "NG", name: "Nigeria", dialCode: "+234", min: 10, max: 10 },
  { code: "BF", name: "Burkina Faso", dialCode: "+226", min: 8, max: 8 },
  { code: "NE", name: "Niger", dialCode: "+227", min: 8, max: 8 },
  { code: "ML", name: "Mali", dialCode: "+223", min: 8, max: 8 },
  { code: "SN", name: "Sénégal", dialCode: "+221", min: 9, max: 9 },
  { code: "GN", name: "Guinée", dialCode: "+224", min: 9, max: 9 },
  { code: "CM", name: "Cameroun", dialCode: "+237", min: 9, max: 9 },
  { code: "GA", name: "Gabon", dialCode: "+241", min: 8, max: 9 },
  { code: "CG", name: "Congo", dialCode: "+242", min: 9, max: 9 },
  { code: "CD", name: "RDC", dialCode: "+243", min: 9, max: 9 },
  { code: "RW", name: "Rwanda", dialCode: "+250", min: 9, max: 9 },
  { code: "KE", name: "Kenya", dialCode: "+254", min: 9, max: 9 },
  { code: "TZ", name: "Tanzanie", dialCode: "+255", min: 9, max: 9 },
  { code: "UG", name: "Ouganda", dialCode: "+256", min: 9, max: 9 },
  { code: "ZA", name: "Afrique du Sud", dialCode: "+27", min: 9, max: 9 },
  { code: "MA", name: "Maroc", dialCode: "+212", min: 9, max: 9 },
  { code: "TN", name: "Tunisie", dialCode: "+216", min: 8, max: 8 },
  { code: "DZ", name: "Algérie", dialCode: "+213", min: 9, max: 9 },
  { code: "EG", name: "Égypte", dialCode: "+20", min: 10, max: 10 },
  { code: "ET", name: "Éthiopie", dialCode: "+251", min: 9, max: 9 },
  { code: "ZM", name: "Zambie", dialCode: "+260", min: 9, max: 9 },
  { code: "ZW", name: "Zimbabwe", dialCode: "+263", min: 9, max: 9 },
  { code: "AO", name: "Angola", dialCode: "+244", min: 9, max: 9 },
  { code: "MZ", name: "Mozambique", dialCode: "+258", min: 8, max: 9 },
  { code: "MG", name: "Madagascar", dialCode: "+261", min: 9, max: 9 },
  { code: "MU", name: "Maurice", dialCode: "+230", min: 7, max: 8 },
];

function getCountryByCode(code) {
  return AFRICAN_COUNTRIES.find((item) => item.code === code) || AFRICAN_COUNTRIES[0];
}

function cleanLocalPhone(value) {
  return String(value || "").replace(/[^0-9]/g, "");
}

function validateAfricanPhoneByCountry(localPhone, countryCode, fieldLabel = "Numéro") {
  const country = getCountryByCode(countryCode);
  const digits = cleanLocalPhone(localPhone);

  if (!digits) {
    return {
      valid: false,
      message: `${fieldLabel} obligatoire.`,
    };
  }

  if (digits.length < country.min || digits.length > country.max) {
    return {
      valid: false,
      message: `${fieldLabel} invalide pour ${country.name}. Le numéro doit contenir ${
        country.min === country.max ? country.min : `${country.min} à ${country.max}`
      } chiffres après l’indicatif ${country.dialCode}.`,
    };
  }

  return {
    valid: true,
    value: `${country.dialCode}${digits}`,
    country: country.code,
    countryName: country.name,
    dialCode: country.dialCode,
    nationalNumber: digits,
  };
}

function validateOptionalAfricanPhoneByCountry(localPhone, countryCode, fieldLabel = "Numéro") {
  const digits = cleanLocalPhone(localPhone);

  if (!digits) {
    const country = getCountryByCode(countryCode);
    return {
      valid: true,
      value: null,
      country: country.code,
      countryName: country.name,
      dialCode: country.dialCode,
      nationalNumber: null,
    };
  }

  return validateAfricanPhoneByCountry(digits, countryCode, fieldLabel);
}

function safeFileName(name = "file") {
  return String(name).replace(/[^\w.\-]+/g, "_");
}

function normalizeAffiliateRef(refValue) {
  if (!refValue) return null;
  const clean = String(refValue).trim();
  if (!clean) return null;
  return clean.replace(/[^a-zA-Z0-9_\-]/g, "").slice(0, 80) || null;
}

async function uploadImageToStorage(file, pathPrefix) {
  if (!file) return null;
  const fileName = `${Date.now()}_${safeFileName(file.name)}`;
  const storageRef = ref(storage, `${pathPrefix}/${fileName}`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
}

function clamp(n, min, max) {
  const v = Number(n);
  if (Number.isNaN(v)) return min;
  return Math.min(max, Math.max(min, v));
}

function formatFCFA(amount) {
  const n = Number(amount || 0);
  return `${n.toLocaleString("fr-FR")} FCFA`;
}

function billingLabel(pack) {
  switch (pack.billingUnit) {
    case "month":
      return "mois";
    case "semester":
      return "semestre";
    case "year":
      return "an";
    default:
      return "période";
  }
}

function PackFeaturePill({ children, featured }) {
  return (
    <span className={`iv-pack-mini-pill ${featured ? "is-featured" : ""}`}>
      {children}
    </span>
  );
}

function loadMetaPixel(pixelId) {
  if (!pixelId || typeof window === "undefined") return;

  if (!window.fbq) {
    window.fbq = function () {
      window.fbq.callMethod
        ? window.fbq.callMethod.apply(window.fbq, arguments)
        : window.fbq.queue.push(arguments);
    };

    if (!window._fbq) window._fbq = window.fbq;
    window.fbq.push = window.fbq;
    window.fbq.loaded = true;
    window.fbq.version = "2.0";
    window.fbq.queue = [];

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }

  window.fbq("init", pixelId);
}

function trackMetaEvent(eventName, params = {}) {
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", eventName, params);
  }
}

export default function InscriptionVendeur() {
  const location = useLocation();
  const navigate = useNavigate();
  const [affiliatePixel, setAffiliatePixel] = useState(null);
  const [affiliateClickId, setAffiliateClickId] = useState(null);
  const [affiliateLinkData, setAffiliateLinkData] = useState(null);
  const selectedPackId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const pack = params.get("pack");
    if (pack && PACKS[pack]) return pack;
    return "standard";
  }, [location.search]);

  // ✅ Affiliation : accepte ?ref=CODE ou ?aff=CODE
  const affiliateRef = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return normalizeAffiliateRef(params.get("ref") || params.get("aff"));
  }, [location.search]);

  useEffect(() => {
    const loadAffiliateTracking = async () => {
      if (!affiliateRef) return;

      try {
        const linkRef = doc(db, "affiliateLinks", affiliateRef);
        const linkSnap = await getDoc(linkRef);

        if (!linkSnap.exists()) return;

        const linkData = linkSnap.data();
        setAffiliateLinkData(linkData);

        const clickDoc = await addDoc(collection(db, "affiliateClicks"), {
          affiliateCode: affiliateRef,
          affiliateOwnerUid: linkData.ownerUid || null,
          page: "inscription-vendeur",
          landingPath: location.pathname,
          landingSearch: location.search,
          userAgent: navigator.userAgent || null,
          converted: false,
          createdAt: serverTimestamp(),
        });

        setAffiliateClickId(clickDoc.id);

        await updateDoc(linkRef, {
          totalClicks: increment(1),
          updatedAt: serverTimestamp(),
        });

        const marketing = linkData.affiliateMarketing;

        if (marketing?.metaPixelEnabled && marketing?.metaPixelId) {
          setAffiliatePixel({
            metaPixelId: marketing.metaPixelId,
            enabled: true,
            affiliateCode: affiliateRef,
          });

          loadMetaPixel(marketing.metaPixelId);

          trackMetaEvent("PageView", {
            content_name: "Inscription vendeur GoShop",
            affiliate_code: affiliateRef,
          });

          trackMetaEvent("ViewContent", {
            content_name: "Page inscription vendeur",
            content_category: "vendor_registration",
            affiliate_code: affiliateRef,
          });
        }
      } catch (error) {
        console.error("Erreur tracking affiliation:", error);
      }
    };

    loadAffiliateTracking();
  }, [affiliateRef, location.pathname, location.search]);

  const selectedPack = PACKS[selectedPackId];

  const [packQuantity, setPackQuantity] = useState(
    selectedPack.allowQuantity ? selectedPack.minQuantity : 1
  );

  useEffect(() => {
    setPackQuantity(selectedPack.allowQuantity ? selectedPack.minQuantity : 1);
  }, [selectedPackId, selectedPack.allowQuantity, selectedPack.minQuantity]);

  const [formData, setFormData] = useState({
    shopName: "",
    fullName: "",
    phoneCountry: "TG",
    phone: "",
    email: "",
    city: "",
    categories: "",
    paymentMethod: "mobile_money",
    mobileMoneyCountry: "TG",
    mobileMoneyNumber: "",
    pack: selectedPackId,
  });

  useEffect(() => {
    setFormData((prev) => ({ ...prev, pack: selectedPackId }));
  }, [selectedPackId]);

  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);

  const [loading, setLoading] = useState(false);
  const [serverMsg, setServerMsg] = useState(null);
  const [serverError, setServerError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePackChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, pack: value }));
    const pk = PACKS[value] || PACKS.standard;
    setPackQuantity(pk.allowQuantity ? pk.minQuantity : 1);
  };

  const onPickLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLogoFile(f);
    setLogoPreview(URL.createObjectURL(f));
  };

  const onPickBanner = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBannerFile(f);
    setBannerPreview(URL.createObjectURL(f));
  };

  const chosenPack = useMemo(
    () => PACKS[formData.pack] || PACKS.standard,
    [formData.pack]
  );

  const qtyMin = chosenPack.allowQuantity ? chosenPack.minQuantity : 1;
  const qtyMax = chosenPack.allowQuantity ? chosenPack.maxQuantity : 1;

  const safeQty = useMemo(
    () => clamp(packQuantity, qtyMin, qtyMax),
    [packQuantity, qtyMin, qtyMax]
  );

  const pricing = useMemo(() => {
    const unitPrice = Number(chosenPack.unitPrice || 0);
    const total = unitPrice * (chosenPack.allowQuantity ? safeQty : 1);
    const totalMonths =
      (chosenPack.unitCount || 1) * (chosenPack.allowQuantity ? safeQty : 1);

    const label = billingLabel(chosenPack);
    const qtyLabel = chosenPack.allowQuantity
      ? `${safeQty} ${label}${safeQty > 1 ? "s" : ""}`
      : label;

    return {
      unitPrice,
      total,
      totalMonths,
      qtyLabel,
      perLabel: label,
    };
  }, [chosenPack, safeQty]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setServerMsg(null);
    setServerError(null);

    const phoneCheck = validateAfricanPhoneByCountry(
      formData.phone,
      formData.phoneCountry,
      "Téléphone WhatsApp"
    );

    if (!phoneCheck.valid) {
      setServerError(phoneCheck.message);
      setLoading(false);
      return;
    }

    const mobileMoneyCheck =
      formData.paymentMethod === "mobile_money"
        ? validateOptionalAfricanPhoneByCountry(
            formData.mobileMoneyNumber,
            formData.mobileMoneyCountry,
            "Numéro Mobile Money"
          )
        : {
            valid: true,
            value: null,
            country: null,
            nationalNumber: null,
          };

    if (!mobileMoneyCheck.valid) {
      setServerError(mobileMoneyCheck.message);
      setLoading(false);
      return;
    }

    try {
      const pack = chosenPack;
      const basePath = `vendorRequestsAssets/${safeFileName(
        formData.shopName || "shop"
      )}_${Date.now()}`;

      const [logoUrl, bannerUrl] = await Promise.all([
        uploadImageToStorage(logoFile, `${basePath}/logo`),
        uploadImageToStorage(bannerFile, `${basePath}/banner`),
      ]);

      const quantity = pack.allowQuantity ? safeQty : 1;

      const payload = {
        shopName: formData.shopName.trim(),
        fullName: formData.fullName.trim(),
        phone: phoneCheck.value,
        phoneCountry: phoneCheck.country,
        phoneCountryName: phoneCheck.countryName,
        phoneDialCode: phoneCheck.dialCode,
        phoneNationalNumber: phoneCheck.nationalNumber,
        email: formData.email.trim() || null,
        city: formData.city.trim(),
        categories: formData.categories.trim() || null,

        logoUrl: logoUrl || null,
        bannerUrl: bannerUrl || null,

        packId: pack.id,
        packName: pack.name,
        packSubtitle: pack.subtitle,
        billingUnit: pack.billingUnit,
        unitCount: pack.unitCount,
        unitPrice: pack.unitPrice,
        quantity,
        totalMonths: pricing.totalMonths,
        totalFee: pricing.total,
        commissionRate: pack.commissionRate,

        paymentMethod: formData.paymentMethod,
        mobileMoneyNumber:
          formData.paymentMethod === "mobile_money"
            ? mobileMoneyCheck.value
            : null,
        mobileMoneyCountry:
          formData.paymentMethod === "mobile_money"
            ? mobileMoneyCheck.country
            : null,
        mobileMoneyCountryName:
          formData.paymentMethod === "mobile_money"
            ? mobileMoneyCheck.countryName
            : null,
        mobileMoneyDialCode:
          formData.paymentMethod === "mobile_money"
            ? mobileMoneyCheck.dialCode
            : null,
        mobileMoneyNationalNumber:
          formData.paymentMethod === "mobile_money"
            ? mobileMoneyCheck.nationalNumber
            : null,

        // ✅ Affiliation / commercial / vendeur parrain
        affiliateRef: affiliateRef || null,
        affiliate:
          affiliateRef
            ? {
              refCode: affiliateRef,
              source: "vendor_registration_link",
              landingPath: location.pathname,
              landingSearch: location.search,
              capturedAt: new Date().toISOString(),
            }
            : null,
        affiliatePixel:
          affiliatePixel?.enabled
            ? {
              metaPixelId: affiliatePixel.metaPixelId,
              enabled: true,
            }
            : null,
        affiliateTracking: affiliateRef
          ? {
            clickId: affiliateClickId || null,
            ownerUid: affiliateLinkData?.ownerUid || null,
            affiliateCode: affiliateRef,
          }
          : null,

        status: "pending_admin_validation",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const requestRef = await addDoc(collection(db, "vendorRequests"), payload);

      if (affiliateRef) {
        await updateDoc(doc(db, "affiliateLinks", affiliateRef), {
          totalRequests: increment(1),
          updatedAt: serverTimestamp(),
        });

        if (affiliateLinkData?.ownerUid) {
          await updateDoc(doc(db, "users", affiliateLinkData.ownerUid), {
            "affiliateStats.requests": increment(1),
            updatedAt: serverTimestamp(),
          });
        }

        if (affiliateClickId) {
          await updateDoc(doc(db, "affiliateClicks", affiliateClickId), {
            converted: true,
            vendorRequestId: requestRef.id,
            convertedAt: serverTimestamp(),
          });
        }
      }

      if (affiliatePixel?.enabled && affiliatePixel?.metaPixelId) {
        trackMetaEvent("Lead", {
          content_name: "Demande vendeur GoShop",
          content_category: "vendor_registration",
          affiliate_code: affiliateRef,
        });

        trackMetaEvent("CompleteRegistration", {
          content_name: "Inscription vendeur envoyée",
          status: "pending_admin_validation",
          affiliate_code: affiliateRef,
        });
      }

      setServerMsg(
        "Votre demande d'inscription a bien été envoyée ✅ Un administrateur va valider votre boutique."
      );

      navigate("/inscription-vendeur/success", {
        state: {
          shopName: formData.shopName.trim(),
          fullName: formData.fullName.trim(),
          phone: phoneCheck.value,
          email: formData.email.trim() || null,
          city: formData.city.trim(),
          packName: pack.name,
          totalFee: pricing.total,
          quantity,
          totalMonths: pricing.totalMonths,
          paymentMethod: formData.paymentMethod,
          status: "pending_admin_validation",
          affiliateRef: affiliateRef || null,
        },
      });

      setFormData((prev) => ({
        ...prev,
        shopName: "",
        fullName: "",
        phone: "",
        email: "",
        city: "",
        categories: "",
        mobileMoneyNumber: "",
      }));

      setPackQuantity(chosenPack.allowQuantity ? chosenPack.minQuantity : 1);
      setLogoFile(null);
      setBannerFile(null);
      setLogoPreview(null);
      setBannerPreview(null);
    } catch (error) {
      console.error(error);
      setServerError(
        "Une erreur est survenue lors de l’envoi de la demande. Merci de réessayer."
      );
    } finally {
      setLoading(false);
    }
  };

  const topPack = selectedPack;

  return (
    <div className="iv-page">
      {/* HERO */}
      <section className="iv-section-full iv-hero-section">
        <div className="iv-section-inner">
          <header className="iv-header">
            <div className="iv-breadcrumb">
              <Link to="/" className="iv-breadcrumb-link">
                Accueil
              </Link>
              <span className="iv-breadcrumb-sep">/</span>
              <Link to="/marketplace" className="iv-breadcrumb-link">
                GoShop Marketplace
              </Link>
              <span className="iv-breadcrumb-sep">/</span>
              <span className="iv-breadcrumb-current">Inscription vendeur</span>
            </div>

            <div className="iv-hero-top">
              <div className="iv-hero-copy">
                <span className="iv-kicker">Rejoindre GoShop</span>
                <h1 className="iv-title">Créez votre boutique vendeur sur GoShop</h1>
                <p className="iv-subtitle">
                  Soumettez votre demande, présentez votre boutique avec un logo
                  et une bannière, choisissez votre pack et démarrez avec une
                  image professionnelle.
                </p>

                {affiliateRef && (
                  <div className="iv-affiliate-box">
                    <span>Invitation vendeur</span>
                    <strong>Code affiliation : {affiliateRef}</strong>
                    <p>
                      Votre inscription sera rattachée au commercial ou vendeur
                      qui vous a partagé ce lien.
                    </p>
                  </div>
                )}
              </div>

              <div className="iv-hero-badge-card">
                <div className="iv-hero-badge">Validation admin</div>
                <strong>Votre demande est étudiée avant activation</strong>
                <p>
                  Un administrateur vérifie votre profil, votre pack et vos
                  informations avant de vous ouvrir l’accès vendeur.
                </p>
              </div>
            </div>
          </header>

          {/* Pack résumé */}
          <div className="iv-pack-summary">
            <div className={"iv-pack-pill " + (topPack.featured ? "is-featured" : "")}>
              Pack sélectionné {topPack.featured ? "· Recommandé" : ""}
            </div>

            <div className="iv-pack-summary-grid">
              <div>
                <h2 className="iv-pack-name">{topPack.name}</h2>
                <p className="iv-pack-price">{topPack.price}</p>
                <p className="iv-pack-subtitle">{topPack.subtitle}</p>
                <p className="iv-pack-description">{topPack.description}</p>
              </div>

              <div className="iv-pack-summary-side">
                <PackFeaturePill featured={topPack.featured}>
                  Commission sur chaque vente
                </PackFeaturePill>
                <PackFeaturePill>Unité : {billingLabel(topPack)}</PackFeaturePill>
                <PackFeaturePill>
                  {topPack.isFree
                    ? "Sans abonnement mensuel"
                    : `Base : ${formatFCFA(topPack.unitPrice)}`}
                </PackFeaturePill>
              </div>
            </div>

            <div className="iv-pack-switch">
              <span>Changer de pack :</span>
              <div className="iv-pack-switch-buttons">
                {Object.values(PACKS).map((p) => {
                  const query = affiliateRef
                    ? `/inscription-vendeur?pack=${p.id}&ref=${encodeURIComponent(affiliateRef)}`
                    : `/inscription-vendeur?pack=${p.id}`;

                  return (
                    <Link
                      key={p.id}
                      to={query}
                      style={baseButtonStyle}
                      className={
                        "iv-pack-switch-btn" +
                        (selectedPackId === p.id ? " is-active" : "")
                      }
                    >
                      {p.id === "standard"
                        ? "Standard"
                        : p.id === "standard_plus"
                          ? "Standard+"
                          : p.id === "pro_semestre"
                            ? "Pro 6 mois"
                            : "Pro Annuel"}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="iv-section-full iv-form-section">
        <div className="iv-section-inner">
          <div className="iv-form-layout">
            {/* FORM CARD */}
            <div className="iv-form-card">
              <div className="iv-form-head">
                <div>
                  <h2 className="iv-form-title">Informations de la boutique</h2>
                  <p className="iv-form-intro">
                    Ajoutez votre identité de boutique, choisissez votre pack et
                    envoyez votre demande.
                  </p>
                </div>

                <div className="iv-form-step">
                  <span>Étape unique</span>
                  <strong>Demande vendeur</strong>
                </div>
              </div>

              {serverMsg && (
                <div className="iv-alert iv-alert-success">{serverMsg}</div>
              )}
              {serverError && (
                <div className="iv-alert iv-alert-error">{serverError}</div>
              )}

              {affiliateRef && (
                <div className="iv-alert iv-alert-affiliate">
                  Cette demande sera associée au code affiliation :{" "}
                  <strong>{affiliateRef}</strong>
                </div>
              )}

              {/* Preview */}
              <div className="iv-preview-card">
                <div className="iv-preview-header">
                  <div>
                    <h3>Aperçu de votre boutique</h3>
                    <p>Visualisez le rendu de votre bannière et de votre logo.</p>
                  </div>
                </div>

                <div className="iv-media-grid">
                  <div className="iv-media-banner">
                    <div className="iv-media-label">Bannière</div>
                    <div className="iv-media-bannerBox">
                      {bannerPreview ? (
                        <img
                          src={bannerPreview}
                          alt="Aperçu bannière"
                          className="iv-media-bannerImg"
                        />
                      ) : (
                        <div className="iv-media-placeholder">
                          Ajoutez une bannière (1600×900 recommandé)
                        </div>
                      )}

                      <div className="iv-media-logoCircle">
                        {logoPreview ? (
                          <img
                            src={logoPreview}
                            alt="Aperçu logo"
                            className="iv-media-logoImg"
                          />
                        ) : (
                          <div className="iv-media-logoPlaceholder">Logo</div>
                        )}
                      </div>

                      <div className="iv-preview-shopname">
                        {formData.shopName || "Nom de votre boutique"}
                      </div>
                    </div>
                  </div>

                  <div className="iv-media-actions">
                    <div className="iv-field">
                      <label className="iv-label">
                        Logo (PNG/JPG) <span className="iv-required">*</span>
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={onPickLogo}
                        className="iv-input"
                        required
                      />
                      <p className="iv-help">
                        Recommandé : format carré 512×512.
                      </p>
                    </div>

                    <div className="iv-field">
                      <label className="iv-label">
                        Bannière (PNG/JPG) <span className="iv-required">*</span>
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={onPickBanner}
                        className="iv-input"
                        required
                      />
                      <p className="iv-help">
                        Recommandé : 1600×900 ou 1920×1080.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="iv-form">
                <div className="iv-block">
                  <div className="iv-block-head">
                    <h3>1. Informations principales</h3>
                    <p>Renseignez l’identité de votre boutique et du responsable.</p>
                  </div>

                  <div className="iv-form-grid">
                    <div className="iv-field">
                      <label className="iv-label">
                        Nom de la boutique <span className="iv-required">*</span>
                      </label>
                      <input
                        type="text"
                        name="shopName"
                        value={formData.shopName}
                        onChange={handleChange}
                        className="iv-input"
                        placeholder="Ex : Le Store"
                        required
                      />
                    </div>

                    <div className="iv-field">
                      <label className="iv-label">
                        Nom complet du responsable{" "}
                        <span className="iv-required">*</span>
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        className="iv-input"
                        placeholder="Nom & prénom"
                        required
                      />
                    </div>

                    <div className="iv-field">
                      <label className="iv-label">
                        Téléphone (WhatsApp){" "}
                        <span className="iv-required">*</span>
                      </label>

                      <div className="iv-phone-row">
                        <select
                          name="phoneCountry"
                          value={formData.phoneCountry}
                          onChange={handleChange}
                          className="iv-country-select"
                          required
                        >
                          {AFRICAN_COUNTRIES.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.name} ({country.dialCode})
                            </option>
                          ))}
                        </select>

                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              phone: cleanLocalPhone(e.target.value),
                            }))
                          }
                          className="iv-input iv-phone-input"
                          placeholder="Ex : 90000000"
                          required
                        />
                      </div>

                      <p className="iv-help">
                        Choisissez le pays puis entrez le numéro sans l’indicatif.
                      </p>
                    </div>

                    <div className="iv-field">
                      <label className="iv-label">E-mail</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="iv-input"
                        placeholder="Ex : boutique@goshop.com"
                      />
                    </div>

                    <div className="iv-field">
                      <label className="iv-label">
                        Ville / Pays <span className="iv-required">*</span>
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="iv-input"
                        placeholder="Ex : Lomé, Togo"
                        required
                      />
                    </div>

                    <div className="iv-field" style={{ gridColumn: "1 / -1" }}>
                      <label className="iv-label">
                        Catégories principales que vous souhaitez vendre
                      </label>
                      <textarea
                        name="categories"
                        value={formData.categories}
                        onChange={handleChange}
                        className="iv-textarea"
                        rows={3}
                        placeholder="Ex : Mode, Chaussures, Beauté, Accessoires..."
                      />
                    </div>
                  </div>
                </div>

                {/* Pack */}
                <div className="iv-block">
                  <div className="iv-block-head">
                    <h3>2. Choix du pack</h3>
                    <p>
                      Sélectionnez la formule qui correspond à votre activité.
                    </p>
                  </div>

                  <div className="iv-pack-choice-options">
                    {Object.values(PACKS).map((p) => (
                      <label
                        key={p.id}
                        className={"iv-radio " + (p.featured ? "is-featured" : "")}
                      >
                        <input
                          type="radio"
                          name="pack"
                          value={p.id}
                          checked={formData.pack === p.id}
                          onChange={handlePackChange}
                        />
                        <span>
                          <strong>{p.name}</strong> — {p.price} ·{" "}
                          <span style={{ color: "#6b7280" }}>{p.subtitle}</span>
                          {p.featured ? (
                            <span className="iv-tag">Recommandé</span>
                          ) : null}
                        </span>
                      </label>
                    ))}
                  </div>

                  {!chosenPack.isFree && chosenPack.allowQuantity && (
                    <div className="iv-qty-wrap">
                      <div className="iv-qty-head">
                        <div className="iv-qty-title">Durée / quantité</div>
                        <div className="iv-qty-sub">
                          Choisissez le nombre de {billingLabel(chosenPack)}
                          {safeQty > 1 ? "s" : ""}.
                        </div>
                      </div>

                      <div className="iv-qty-row">
                        <button
                          type="button"
                          className="iv-qty-btn"
                          onClick={() =>
                            setPackQuantity((q) =>
                              clamp(Number(q) - 1, qtyMin, qtyMax)
                            )
                          }
                          aria-label="Diminuer"
                        >
                          −
                        </button>

                        <input
                          type="number"
                          min={qtyMin}
                          max={qtyMax}
                          value={safeQty}
                          onChange={(e) =>
                            setPackQuantity(clamp(e.target.value, qtyMin, qtyMax))
                          }
                          className="iv-qty-input"
                        />

                        <button
                          type="button"
                          className="iv-qty-btn"
                          onClick={() =>
                            setPackQuantity((q) =>
                              clamp(Number(q) + 1, qtyMin, qtyMax)
                            )
                          }
                          aria-label="Augmenter"
                        >
                          +
                        </button>

                        <div className="iv-qty-meta">
                          <div>
                            <span className="iv-qty-label">Total :</span>{" "}
                            <strong>{formatFCFA(pricing.total)}</strong>
                          </div>
                          <div className="iv-qty-small">
                            {formatFCFA(pricing.unitPrice)} × {safeQty} (
                            {pricing.qtyLabel})
                          </div>
                        </div>
                      </div>

                      <div className="iv-qty-hint">
                        Exemple : pour le <strong>Pro Annuel</strong>, choisissez 2
                        pour payer <strong>2 ans</strong>.
                      </div>
                    </div>
                  )}
                </div>

                {/* Paiement */}
                <div className="iv-block">
                  <div className="iv-block-head">
                    <h3>3. Méthode de prélèvement</h3>
                    <p>
                      Choisissez comment seront gérés vos abonnements ou frais.
                    </p>
                  </div>

                  <div className="iv-pack-choice-options">
                    <label className="iv-radio">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="mobile_money"
                        checked={formData.paymentMethod === "mobile_money"}
                        onChange={handleChange}
                      />
                      <span>Mobile Money (recommandé)</span>
                    </label>

                    <label className="iv-radio">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="card"
                        checked={formData.paymentMethod === "card"}
                        onChange={handleChange}
                      />
                      <span>Carte bancaire</span>
                    </label>
                  </div>

                  {formData.paymentMethod === "mobile_money" && (
                    <div className="iv-field" style={{ marginTop: 8 }}>
                      <label className="iv-label">
                        Numéro Mobile Money (prélèvements)
                      </label>
                      <div className="iv-phone-row">
                        <select
                          name="mobileMoneyCountry"
                          value={formData.mobileMoneyCountry}
                          onChange={handleChange}
                          className="iv-country-select"
                        >
                          {AFRICAN_COUNTRIES.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.name} ({country.dialCode})
                            </option>
                          ))}
                        </select>

                        <input
                          type="tel"
                          name="mobileMoneyNumber"
                          value={formData.mobileMoneyNumber}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              mobileMoneyNumber: cleanLocalPhone(e.target.value),
                            }))
                          }
                          className="iv-input iv-phone-input"
                          placeholder="Ex : 90000000"
                        />
                      </div>

                      <p className="iv-help">
                        Facultatif. Choisissez le pays puis entrez le numéro sans l’indicatif.
                      </p>
                    </div>
                  )}
                </div>

                <div className="iv-submit-row">
                  <button
                    type="submit"
                    style={baseButtonStyle}
                    className="iv-submit-btn"
                    disabled={loading}
                  >
                    {loading ? "Envoi en cours..." : "Envoyer ma demande"}
                  </button>

                  <p className="iv-submit-note">
                    Après validation, vous recevrez la marche à suivre pour
                    activer votre compte vendeur.
                  </p>
                </div>
              </form>
            </div>

            {/* ASIDE */}
            <aside className="iv-aside">
              {affiliateRef && (
                <div className="iv-aside-card iv-aside-affiliate">
                  <h3 className="iv-aside-title">Lien d’affiliation détecté</h3>
                  <p className="iv-aside-text">
                    Cette inscription est rattachée au code :
                  </p>
                  <div className="iv-affiliate-code">{affiliateRef}</div>
                </div>
              )}

              <div className="iv-aside-card">
                <h3 className="iv-aside-title">Après votre demande</h3>
                <ol className="iv-aside-list">
                  <li>Un admin vérifie vos informations.</li>
                  <li>Validation ou refus selon la stratégie GoShop.</li>
                  <li>Si validé : création de compte vendeur + accès dashboard.</li>
                  <li>Pack appliqué (abonnement / commissions).</li>
                  {affiliateRef && <li>Le commercial affilié est rattaché à la demande.</li>}
                </ol>
              </div>

              <div className="iv-aside-card iv-aside-card-light">
                <h3 className="iv-aside-title">Ce qu’il faut préparer</h3>
                <ul className="iv-aside-list">
                  <li>Nom de votre boutique</li>
                  <li>Numéro WhatsApp actif</li>
                  <li>Logo de boutique</li>
                  <li>Bannière ou visuel principal</li>
                  <li>Catégories de produits</li>
                </ul>
              </div>

              <div className="iv-aside-card iv-aside-card-light">
                <h3 className="iv-aside-title">Assistance</h3>
                <p className="iv-aside-text">
                  Une question sur les packs ou l’inscription ?
                </p>
                <p className="iv-aside-contact">
                  WhatsApp : <strong>+228 XX XX XX XX</strong>
                  <br />
                  E-mail : <strong>support@goshopforall.com</strong>
                </p>
              </div>

              <div className="iv-aside-card iv-aside-pro">
                <div className="iv-aside-pro-badge">Pack Pro recommandé</div>
                <div className="iv-aside-pro-title">
                  Pro Annuel : 15 000 FCFA / an
                </div>
                <div className="iv-aside-pro-text">
                  Meilleure valeur : ≈ <strong>1 250 FCFA/mois</strong>.
                  <br />
                  Vous pouvez sélectionner <strong>2 ans</strong> ou plus.
                </div>
                <Link
                  to={
                    affiliateRef
                      ? `/inscription-vendeur?pack=pro&ref=${encodeURIComponent(affiliateRef)}`
                      : "/inscription-vendeur?pack=pro"
                  }
                  className="iv-aside-pro-btn"
                >
                  Choisir Pro Annuel
                </Link>
              </div>

              <div className="iv-aside-card">
                <h3 className="iv-aside-title">Résumé estimatif</h3>
                <div className="iv-summary-line">
                  <span>Pack</span>
                  <strong>{chosenPack.name}</strong>
                </div>
                <div className="iv-summary-line">
                  <span>Durée</span>
                  <strong>
                    {chosenPack.isFree ? "Flexible" : pricing.qtyLabel}
                  </strong>
                </div>
                <div className="iv-summary-line">
                  <span>Commission</span>
                  <strong>{chosenPack.commissionRate}%</strong>
                </div>
                {affiliateRef && (
                  <div className="iv-summary-line">
                    <span>Affiliation</span>
                    <strong>{affiliateRef}</strong>
                  </div>
                )}
                <div className="iv-summary-line total">
                  <span>Total abonnement</span>
                  <strong>
                    {chosenPack.isFree
                      ? "0 FCFA"
                      : formatFCFA(pricing.total)}
                  </strong>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <style>{`
        .iv-page{
          min-height:100vh;
          background:#f3f4f6;
          color:#111827;
          font-family:'Bogle', Helvetica Neue, Helvetica, Arial, sans-serif;
        }

        .iv-section-full{
          position:relative;
          left:50%;
          right:50%;
          margin-left:-50vw;
          margin-right:-50vw;
          width:100vw;
        }

        .iv-section-inner{
          max-width:1160px;
          margin:0 auto;
          padding:24px 16px;
        }

        @media(max-width:768px){
          .iv-section-inner{padding:16px 12px;}
        }

        .iv-hero-section{
          background:
            radial-gradient(circle at top left, #fff7ed 0, #f3f4f6 45%, #eef2ff 100%);
          border-bottom:1px solid #e5e7eb;
        }

        .iv-header{margin-bottom:18px;}

        .iv-breadcrumb{
          display:flex;
          flex-wrap:wrap;
          align-items:center;
          gap:4px;
          font-size:12px;
          color:#6b7280;
          margin-bottom:10px;
        }

        .iv-breadcrumb-link{
          color:#4b5563;
          text-decoration:none;
        }

        .iv-breadcrumb-link:hover{
          color:#111827;
          text-decoration:underline;
        }

        .iv-breadcrumb-current{
          color:#111827;
          font-weight:700;
        }

        .iv-breadcrumb-sep{color:#9ca3af;}

        .iv-hero-top{
          display:grid;
          grid-template-columns:minmax(0,1.4fr) minmax(280px,.6fr);
          gap:16px;
          align-items:start;
        }

        .iv-kicker{
          display:inline-flex;
          padding:5px 10px;
          border-radius:999px;
          background:#ede9fe;
          color:#5b21b6;
          font-size:11px;
          font-weight:900;
          text-transform:uppercase;
          letter-spacing:.08em;
          margin-bottom:10px;
        }

        .iv-title{
          font-size:30px;
          font-weight:900;
          color:#0f172a;
          line-height:1.15;
          margin:0 0 8px;
        }

        .iv-subtitle{
          font-size:14px;
          color:#4b5563;
          max-width:760px;
          margin:0;
        }

        .iv-affiliate-box{
          margin-top:14px;
          display:inline-flex;
          flex-direction:column;
          gap:3px;
          max-width:520px;
          padding:12px 14px;
          border-radius:18px;
          background:#ecfdf5;
          border:1px solid #bbf7d0;
          color:#14532d;
        }

        .iv-affiliate-box span{
          font-size:11px;
          font-weight:900;
          text-transform:uppercase;
          letter-spacing:.06em;
        }

        .iv-affiliate-box strong{
          font-size:14px;
        }

        .iv-affiliate-box p{
          margin:0;
          font-size:12px;
          color:#166534;
        }

        .iv-hero-badge-card{
          background:#fff;
          border:1px solid #e5e7eb;
          border-radius:20px;
          padding:14px;
          box-shadow:0 16px 36px rgba(15,23,42,0.08);
        }

        .iv-hero-badge{
          display:inline-flex;
          padding:4px 10px;
          border-radius:999px;
          background:#ecfdf5;
          color:#166534;
          font-size:11px;
          font-weight:900;
          margin-bottom:8px;
        }

        .iv-hero-badge-card strong{
          display:block;
          font-size:15px;
          color:#111827;
          margin-bottom:4px;
        }

        .iv-hero-badge-card p{
          margin:0;
          color:#6b7280;
          font-size:13px;
          line-height:1.55;
        }

        @media(max-width:900px){
          .iv-hero-top{
            grid-template-columns:minmax(0,1fr);
          }
        }

        .iv-pack-summary{
          margin-top:18px;
          background:#fff;
          border-radius:24px;
          padding:18px 18px 16px;
          box-shadow:0 16px 36px rgba(15,23,42,0.10);
          border:1px solid #e5e7eb;
        }

        .iv-pack-summary-grid{
          display:grid;
          grid-template-columns:minmax(0,1fr) 280px;
          gap:14px;
          align-items:start;
        }

        .iv-pack-summary-side{
          display:flex;
          flex-wrap:wrap;
          gap:8px;
          align-content:flex-start;
          justify-content:flex-end;
        }

        @media(max-width:768px){
          .iv-pack-summary-grid{
            grid-template-columns:minmax(0,1fr);
          }
          .iv-pack-summary-side{
            justify-content:flex-start;
          }
        }

        .iv-pack-pill{
          display:inline-flex;
          align-items:center;
          padding:4px 10px;
          border-radius:999px;
          background:#fff7ed;
          color:#c2410c;
          font-size:11px;
          font-weight:900;
          text-transform:uppercase;
          letter-spacing:.08em;
          margin-bottom:6px;
        }

        .iv-pack-pill.is-featured{
          background:#ffedd5;
          border:1px solid #fed7aa;
        }

        .iv-pack-name{
          font-size:20px;
          font-weight:900;
          color:#111827;
          margin:0;
        }

        .iv-pack-price{
          font-size:15px;
          font-weight:900;
          color:#16a34a;
          margin:4px 0 0;
        }

        .iv-pack-subtitle{
          font-size:12px;
          color:#6b7280;
          margin:2px 0 6px;
        }

        .iv-pack-description{
          font-size:13px;
          color:#4b5563;
          margin:0;
          max-width:780px;
        }

        .iv-pack-mini-pill{
          display:inline-flex;
          align-items:center;
          padding:6px 10px;
          border-radius:999px;
          background:#f8fafc;
          border:1px solid #e5e7eb;
          color:#334155;
          font-size:12px;
          font-weight:800;
        }

        .iv-pack-mini-pill.is-featured{
          background:#fff7ed;
          border-color:#fed7aa;
          color:#9a3412;
        }

        .iv-pack-switch{
          margin-top:12px;
          font-size:12px;
          color:#4b5563;
        }

        .iv-pack-switch-buttons{
          margin-top:8px;
          display:flex;
          flex-wrap:wrap;
          gap:8px;
        }

        .iv-pack-switch-btn{
          background:#fff;
          color:#111827;
          border:1px solid #d1d5db;
          box-shadow:none;
        }

        .iv-pack-switch-btn.is-active{
          background:#f97316;
          color:#fff;
          border-color:#f97316;
        }

        .iv-pack-switch-btn:hover{
          background:#111827;
          color:#fff;
        }

        .iv-form-section{background:#f3f4f6;}

        .iv-form-layout{
          display:grid;
          grid-template-columns:minmax(0,2.2fr) minmax(300px,.8fr);
          gap:18px;
          align-items:start;
        }

        .iv-form-card{
          background:#fff;
          border-radius:24px;
          padding:18px;
          border:1px solid #e5e7eb;
          box-shadow:0 12px 28px rgba(15,23,42,0.08);
        }

        .iv-form-head{
          display:flex;
          justify-content:space-between;
          gap:14px;
          align-items:flex-start;
          margin-bottom:12px;
        }

        .iv-form-title{
          font-size:20px;
          font-weight:900;
          color:#111827;
          margin:0 0 4px;
        }

        .iv-form-intro{
          font-size:13px;
          color:#4b5563;
          margin:0;
        }

        .iv-form-step{
          background:#f8fafc;
          border:1px solid #e5e7eb;
          border-radius:16px;
          padding:10px 12px;
          min-width:150px;
        }

        .iv-form-step span{
          display:block;
          font-size:11px;
          color:#6b7280;
          text-transform:uppercase;
          font-weight:900;
          letter-spacing:.06em;
        }

        .iv-form-step strong{
          display:block;
          font-size:13px;
          color:#111827;
          margin-top:2px;
        }

        @media(max-width:700px){
          .iv-form-head{
            flex-direction:column;
          }
        }

        .iv-alert{
          border-radius:12px;
          padding:10px 12px;
          font-size:13px;
          margin-bottom:12px;
          font-weight:700;
        }

        .iv-alert-success{
          background:#ecfdf3;
          color:#166534;
          border:1px solid #bbf7d0;
        }

        .iv-alert-error{
          background:#fef2f2;
          color:#b91c1c;
          border:1px solid #fecaca;
        }

        .iv-alert-affiliate{
          background:#eff6ff;
          color:#1d4ed8;
          border:1px solid #bfdbfe;
        }

        .iv-preview-card{
          border:1px solid #e5e7eb;
          border-radius:20px;
          padding:14px;
          background:#fafafa;
          margin-bottom:16px;
        }

        .iv-preview-header h3{
          margin:0;
          font-size:16px;
          font-weight:900;
          color:#111827;
        }

        .iv-preview-header p{
          margin:4px 0 0;
          font-size:12px;
          color:#6b7280;
        }

        .iv-media-grid{
          display:grid;
          grid-template-columns:minmax(0,1.4fr) minmax(0,1fr);
          gap:12px;
          align-items:start;
          margin-top:12px;
        }

        .iv-media-label{
          font-size:12px;
          color:#374151;
          font-weight:900;
          margin-bottom:6px;
        }

        .iv-media-bannerBox{
          position:relative;
          border-radius:18px;
          overflow:hidden;
          border:1px solid #e5e7eb;
          background:#0f172a;
          aspect-ratio:16/6;
          min-height:120px;
        }

        .iv-media-bannerImg{
          width:100%;
          height:100%;
          object-fit:cover;
          display:block;
          filter:saturate(1.05);
        }

        .iv-media-placeholder{
          width:100%;
          height:100%;
          display:flex;
          align-items:center;
          justify-content:center;
          color:rgba(255,255,255,0.85);
          font-weight:900;
          font-size:12px;
          background:linear-gradient(135deg,#111827,#0b1226);
          text-align:center;
          padding:12px;
        }

        .iv-media-logoCircle{
          position:absolute;
          left:14px;
          bottom:12px;
          width:68px;
          height:68px;
          border-radius:18px;
          background:#fff;
          display:flex;
          align-items:center;
          justify-content:center;
          overflow:hidden;
          border:3px solid rgba(255,255,255,0.95);
          box-shadow:0 10px 22px rgba(0,0,0,0.25);
        }

        .iv-media-logoImg{
          width:100%;
          height:100%;
          object-fit:cover;
        }

        .iv-media-logoPlaceholder{
          font-size:12px;
          font-weight:900;
          color:#111827;
        }

        .iv-preview-shopname{
          position:absolute;
          left:96px;
          bottom:22px;
          color:#fff;
          font-size:14px;
          font-weight:900;
          text-shadow:0 2px 10px rgba(0,0,0,.5);
          max-width:60%;
          overflow:hidden;
          text-overflow:ellipsis;
          white-space:nowrap;
        }

        .iv-help{
          font-size:11px;
          color:#6b7280;
          margin:4px 0 0;
        }

        .iv-form{
          display:flex;
          flex-direction:column;
          gap:16px;
        }

        .iv-block{
          border-top:1px solid #e5e7eb;
          padding-top:14px;
        }

        .iv-block:first-of-type{
          border-top:none;
          padding-top:0;
        }

        .iv-block-head{
          margin-bottom:10px;
        }

        .iv-block-head h3{
          margin:0;
          font-size:16px;
          font-weight:900;
          color:#111827;
        }

        .iv-block-head p{
          margin:4px 0 0;
          font-size:12px;
          color:#6b7280;
        }

        .iv-form-grid{
          display:grid;
          grid-template-columns:minmax(0,1fr) minmax(0,1fr);
          gap:12px 14px;
        }

        .iv-field{
          display:flex;
          flex-direction:column;
          gap:5px;
        }

        .iv-label{
          font-size:12px;
          color:#374151;
          font-weight:800;
        }

        .iv-required{color:#dc2626;}

        .iv-input,
        .iv-textarea,
        .iv-country-select{
          font-size:13px;
          padding:10px 12px;
          border-radius:14px;
          border:1px solid #d1d5db;
          background:#fff;
          color:#111827;
          outline:none;
        }

        .iv-phone-row{
          display:grid;
          grid-template-columns: minmax(160px,.72fr) minmax(0,1fr);
          gap:8px;
          align-items:center;
        }

        .iv-country-select{
          width:100%;
          cursor:pointer;
        }

        .iv-phone-input{
          width:100%;
        }

        @media(max-width:600px){
          .iv-phone-row{
            grid-template-columns:minmax(0,1fr);
          }
        }

        .iv-input:focus,
        .iv-textarea:focus,
        .iv-country-select:focus{
          border-color:#f97316;
          box-shadow:0 0 0 1px rgba(249,115,22,0.18);
        }

        .iv-pack-choice-options{
          display:grid;
          gap:8px;
          font-size:12px;
          color:#374151;
        }

        .iv-radio{
          display:flex;
          align-items:flex-start;
          gap:8px;
          padding:12px 12px;
          border:1px solid #e5e7eb;
          border-radius:14px;
          background:#fff;
        }

        .iv-radio:hover{
          border-color:#d1d5db;
          background:#f9fafb;
        }

        .iv-radio input{margin-top:3px;}

        .iv-radio.is-featured{
          border-color:rgba(249,115,22,0.35);
          background:linear-gradient(135deg, rgba(255,237,213,0.65), #fff);
        }

        .iv-tag{
          margin-left:8px;
          display:inline-flex;
          align-items:center;
          font-size:10px;
          font-weight:900;
          padding:2px 8px;
          border-radius:999px;
          background:#ffedd5;
          border:1px solid #fed7aa;
          color:#9a3412;
        }

        .iv-qty-wrap{
          margin-top:10px;
          padding:12px 12px;
          border-radius:16px;
          border:1px dashed rgba(249,115,22,0.35);
          background:#fff7ed;
        }

        .iv-qty-head{
          display:flex;
          flex-direction:column;
          gap:2px;
          margin-bottom:8px;
        }

        .iv-qty-title{
          font-size:13px;
          font-weight:900;
          color:#111827;
        }

        .iv-qty-sub{
          font-size:12px;
          color:#6b7280;
        }

        .iv-qty-row{
          display:flex;
          flex-wrap:wrap;
          gap:10px;
          align-items:center;
        }

        .iv-qty-btn{
          width:36px;
          height:36px;
          border-radius:12px;
          border:1px solid #fed7aa;
          background:#fff;
          font-weight:900;
          font-size:18px;
          cursor:pointer;
          color:#111827;
        }

        .iv-qty-btn:hover{
          background:#111827;
          color:#fff;
          border-color:#111827;
        }

        .iv-qty-input{
          width:72px;
          height:36px;
          border-radius:12px;
          border:1px solid #fed7aa;
          background:#fff;
          text-align:center;
          font-weight:900;
          font-size:14px;
          outline:none;
        }

        .iv-qty-meta{
          display:flex;
          flex-direction:column;
          gap:2px;
        }

        .iv-qty-label{
          color:#6b7280;
          font-size:12px;
        }

        .iv-qty-small{
          color:#6b7280;
          font-size:11px;
        }

        .iv-qty-hint{
          margin-top:8px;
          font-size:11px;
          color:#6b7280;
        }

        .iv-submit-row{
          margin-top:6px;
          display:flex;
          flex-direction:column;
          gap:6px;
        }

        .iv-submit-btn{
          background:linear-gradient(135deg,#f97316 0%,#fb923c 45%,#f97316 100%);
          color:#fff;
          box-shadow:0 10px 24px rgba(249,115,22,0.28);
          border:none;
        }

        .iv-submit-btn:disabled{
          opacity:.8;
          cursor:wait;
        }

        .iv-submit-btn:hover:enabled{
          background:#111827;
          color:#fff;
          box-shadow:0 14px 30px rgba(15,23,42,0.35);
        }

        .iv-submit-note{
          font-size:11px;
          color:#6b7280;
          margin:0;
        }

        .iv-aside{
          display:flex;
          flex-direction:column;
          gap:12px;
        }

        .iv-aside-card{
          background:#fff;
          border-radius:18px;
          padding:14px;
          border:1px solid #e5e7eb;
          font-size:12px;
          color:#4b5563;
          box-shadow:0 8px 18px rgba(15,23,42,0.05);
        }

        .iv-aside-card-light{
          background:#f9fafb;
        }

        .iv-aside-title{
          font-size:14px;
          font-weight:900;
          color:#111827;
          margin:0 0 8px;
        }

        .iv-aside-list{
          margin:0;
          padding-left:18px;
        }

        .iv-aside-list li{margin-bottom:6px;}

        .iv-aside-text{margin:0 0 6px;}
        .iv-aside-contact{margin:0;}

        .iv-aside-affiliate{
          border-color:#bbf7d0;
          background:#ecfdf5;
        }

        .iv-affiliate-code{
          display:inline-flex;
          padding:8px 10px;
          border-radius:12px;
          background:#fff;
          border:1px dashed #16a34a;
          color:#166534;
          font-weight:900;
          max-width:100%;
          overflow:hidden;
          text-overflow:ellipsis;
        }

        .iv-aside-pro{
          border-color:rgba(249,115,22,0.25);
          background:linear-gradient(135deg, rgba(255,237,213,0.55), #fff);
        }

        .iv-aside-pro-badge{
          display:inline-flex;
          width:fit-content;
          font-size:10px;
          font-weight:900;
          padding:2px 8px;
          border-radius:999px;
          background:#ffedd5;
          border:1px solid #fed7aa;
          color:#9a3412;
          margin-bottom:6px;
        }

        .iv-aside-pro-title{
          font-size:13px;
          font-weight:900;
          color:#111827;
          margin-bottom:4px;
        }

        .iv-aside-pro-text{
          font-size:12px;
          color:#4b5563;
          margin-bottom:10px;
        }

        .iv-aside-pro-btn{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          padding:9px 12px;
          border-radius:999px;
          text-decoration:none;
          font-weight:900;
          font-size:12px;
          background:#111827;
          color:#fff;
          border:1px solid #111827;
        }

        .iv-aside-pro-btn:hover{
          background:#f97316;
          border-color:#f97316;
          color:#fff;
        }

        .iv-summary-line{
          display:flex;
          justify-content:space-between;
          gap:10px;
          padding:6px 0;
          border-bottom:1px solid #f1f5f9;
          font-size:12px;
        }

        .iv-summary-line:last-child{
          border-bottom:none;
        }

        .iv-summary-line.total{
          margin-top:4px;
          padding-top:10px;
          font-size:13px;
        }

        @media(max-width:980px){
          .iv-form-layout{
            grid-template-columns:minmax(0,1fr);
          }
        }

        @media(max-width:900px){
          .iv-form-grid{
            grid-template-columns:minmax(0,1fr);
          }
          .iv-media-grid{
            grid-template-columns:minmax(0,1fr);
          }
          .iv-media-bannerBox{
            aspect-ratio:16/7;
          }
        }

        @media(max-width:600px){
          .iv-title{font-size:24px;}
          .iv-pack-summary{padding:14px;}
          .iv-media-logoCircle{
            width:58px;
            height:58px;
            border-radius:16px;
          }
          .iv-preview-shopname{
            left:84px;
            bottom:20px;
            font-size:13px;
            max-width:55%;
          }
        }
      `}</style>
    </div>
  );
}
